import { useEffect, useRef } from 'react';
import { AccountCircle, SentimentSatisfiedAlt } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { Section, SECTION_TYPE } from 'atoms/Section';
import { PATHS } from 'commons/paths';
import { useGetMessages } from 'hooks/u005/useGetMessagesHooks';
import { LinkButton } from 'atoms/LinkButton';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { Message } from 'hooks/u005/useGetMessagesHooks';
import { cvtDatetime } from 'commons/utils';
import { AttachFile } from '@mui/icons-material';

const NUM_OF_DISP = 2;

type Props = {
  userId: string;
  auditionId: string;
};

export const MessageSection = (props: Props) => {
  const messagesRef = useRef<HTMLDivElement>(null);
  const [messages, loading, err] = useGetMessages(props.userId, props.auditionId);
  const { t: tAuditionInfo } = useTranslationWithLang('auditionInfo');
  const { t: tMessage } = useTranslationWithLang('message');

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTo(0, messagesRef.current.scrollHeight);
    }
  }, [messages]);

  const originator = (message: Message) => {
    const dom = message.isUser ? (
      <>
        <SentimentSatisfiedAlt />
        <p className="pl-1">{tAuditionInfo('TU005_012')}</p>
      </>
    ) : (
      <>
        <AccountCircle />
        <p className="pl-1">{tAuditionInfo('TU005_013')}</p>
      </>
    );
    const FileIcon =
      message.file == null || message.file === '' ? (
        <></>
      ) : (
        <div
          className="text-[--c-textinput-label]"
          style={{
            color: `var(${message.isUser ? '--c-message-user-color' : '--c-message-color'})`,
          }}
        >
          <AttachFile style={{ width: '1rem', height: '1rem' }} />
        </div>
      );
    return (
      <div
        className="inline-flex"
        style={{
          color: `var(${message.isUser ? '--c-message-user-color' : '--c-message-color'})`,
        }}
      >
        {dom}
        <p className="pl-1">{cvtDatetime(message.ins_dtm)}</p>
        {FileIcon}
      </div>
    );
  };

  return (
    <Section type={SECTION_TYPE.MESSAGE}>
      <div className="h-full pb-4 pl-4 pt-4 pr-4">
        <Stack spacing={2} className="h-full">
          <div
            className="h-full max-h-[180px] overflow-auto break-words whitespace-pre-wrap"
            ref={messagesRef}
          >
            <Stack spacing={2} className="h-full" component="ul">
              {(() => {
                const domList = [];
                if (err != null) {
                  console.error(err.message);
                  domList.push(
                    <li key="error">
                      <p className="text-[--c-error] font-bold">{tMessage('MU005F0046E')}</p>
                    </li>
                  );
                } else if (messages != null) {
                  const counter = Math.min(NUM_OF_DISP, messages.length);
                  if (counter > 0) {
                    for (let i = 0; i < counter; i++) {
                      const message = [...messages].reverse().slice(-counter)[i];
                      domList.push(
                        <li key={i} className="mb-2">
                          {originator(message)}
                          <div className="whitespace-pre-wrap">{message.message}</div>
                        </li>
                      );
                    }
                  } else if (loading) {
                    domList.push(
                      <li key="loading" className="flex justify-center text-[--c-text2] pt-2">
                        Loading...
                      </li>
                    );
                  } else {
                    domList.push(
                      <li key="notfound">
                        <div>{tMessage('MU005F0047I')}</div>
                      </li>
                    );
                  }
                }
                return domList;
              })()}
            </Stack>
          </div>
          <div className="mt-auto flex justify-center">
            <LinkButton
              title={tAuditionInfo('TU005_004')}
              path={`${PATHS.MESSAGE}/${props.auditionId}`}
            ></LinkButton>
          </div>
        </Stack>
      </div>
    </Section>
  );
};
